import { Children, isValidElement, type ReactNode } from "react";

export interface ComboboxOption {
  value: string;
  label: string;
  disabled?: boolean;
}

function extractOptionLabel(node: ReactNode): string {
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(extractOptionLabel).join("");
  }
  return "";
}

/**
 * Map option children to combobox options: native `<option>` elements and
 * option components such as Select's `SelectOption` (a `value` prop, and a
 * `label` prop or text children).
 */
export function optionsFromSelectChildren(children: ReactNode): ComboboxOption[] {
  const options: ComboboxOption[] = [];

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;

    const props = child.props as {
      value?: string | number;
      label?: string;
      disabled?: boolean;
      children?: ReactNode;
    };
    if (child.type !== "option" && props.value === undefined) return;

    options.push({
      value: String(props.value ?? ""),
      label: extractOptionLabel(props.children) || props.label || "",
      disabled: props.disabled,
    });
  });

  return options;
}

export function splitPlaceholderOption(options: ComboboxOption[]): {
  placeholder?: string;
  options: ComboboxOption[];
} {
  const placeholderOption = options.find((option) => option.value === "");
  if (!placeholderOption) {
    return { options };
  }

  return {
    placeholder: placeholderOption.label,
    options: options.filter((option) => option.value !== ""),
  };
}

/**
 * Index of the next enabled option from `from`, moving by `step` and
 * wrapping at either end, so arrow keys never land on a disabled option.
 * A negative `from` means "nothing highlighted yet". Returns -1 when every
 * option is disabled.
 */
export function stepEnabledOptionIndex(
  options: ReadonlyArray<{ disabled?: boolean }>,
  from: number,
  step: 1 | -1,
): number {
  const count = options.length;
  if (count === 0) return -1;
  const start = from < 0 || from >= count ? (step > 0 ? -1 : count) : from;
  for (let offset = 1; offset <= count; offset += 1) {
    const index = (((start + step * offset) % count) + count) % count;
    if (!options[index].disabled) return index;
  }
  return -1;
}

/** First enabled option index, or -1 when every option is disabled. */
export function firstEnabledOptionIndex(
  options: ReadonlyArray<{ disabled?: boolean }>,
): number {
  return options.findIndex((option) => !option.disabled);
}

/** Last enabled option index, or -1 when every option is disabled. */
export function lastEnabledOptionIndex(
  options: ReadonlyArray<{ disabled?: boolean }>,
): number {
  for (let index = options.length - 1; index >= 0; index -= 1) {
    if (!options[index].disabled) return index;
  }
  return -1;
}

/**
 * True when focus is leaving the field for an element outside both the
 * field and its portaled list, so the list should close.
 */
export function focusLeftField(
  nextFocus: EventTarget | null,
  field: HTMLElement | null,
  list: HTMLElement | null,
): boolean {
  if (!(nextFocus instanceof Node)) return false;
  if (field?.contains(nextFocus)) return false;
  if (list?.contains(nextFocus)) return false;
  return true;
}
